import { EVC_ADDRESS } from '@/config/euler-contracts'
import { formFormat } from '@/domain/common/format'
import { isNativeHypeAddress, isWhypeAddress } from '@/domain/euler/nativeHype'
import { FALLBACK_SUBACCOUNT_RANGE, getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { getFirstAvailableSubaccountId, useSubaccountStore } from '@/domain/euler/subaccounts'
import { useSubaccountControllers } from '@/domain/euler/subaccounts/useSubaccountControllers'
import { usePositionNames } from '@/domain/state'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { Objective } from '@/features/actions/logic/types'
import { DialogActionsPanel } from '@/features/dialogs/common/components/DialogActionsPanel'
import { FormAndOverviewWrapper } from '@/features/dialogs/common/components/FormAndOverviewWrapper'
import { DialogForm } from '@/features/dialogs/common/components/form/DialogForm'
import { SuccessViewCheckmark } from '@/features/dialogs/common/components/success-view/SuccessViewCheckmark'
import { SuccessViewContent } from '@/features/dialogs/common/components/success-view/SuccessViewContent'
import { SuccessViewProceedButton } from '@/features/dialogs/common/components/success-view/SuccessViewProceedButton'
import { SuccessViewSummaryPanel } from '@/features/dialogs/common/components/success-view/SuccessViewSummaryPanel'
import { CommonDialogProps, DialogConfig } from '@/features/dialogs/common/types'
import { WithdrawOverviewPanel } from '@/features/dialogs/withdraw/components/WithdrawOverviewPanel'
import { useWithdrawDialog } from '@/features/dialogs/withdraw/logic/useWithdrawDialog'
import { useBorrowPairs } from '@/features/eborrow/logic/useBorrowPairs'
import { getVaultMarket } from '@/features/evault-details/data/products'
import { VaultType } from '@/features/usdxl/types/vaults'
import { Dialog, DialogContent, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { RiskAcknowledgement } from '@/ui/organisms/risk-acknowledgement/RiskAcknowledgement'
import { cn } from '@/ui/utils/style'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { ChevronDown, Plus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Address, formatUnits } from 'viem'
import { useAccount, useChainId, useReadContracts } from 'wagmi'

export interface PooledMigrationTarget {
  label: 'Earn' | 'Prime' | 'Yield'
  vaultType: 'eEarn' | 'eLend'
  vault: {
    address: Address
    underlyingAsset: Address
  }
}

export interface PooledMigrationDialogProps extends CommonDialogProps {
  token: Token
  target: PooledMigrationTarget
}

function PooledMigrationDialog({ token, target, open, setOpen }: PooledMigrationDialogProps) {
  const chainId = useChainId()
  const migrationToken = useMemo(() => getMigrationToken({ token, target, chainId }), [chainId, target, token])
  const usesWrappedNativeMigration = migrationToken.address.toLowerCase() !== token.address.toLowerCase()
  const withdrawDialog = useWithdrawDialog({ initialToken: migrationToken })
  const maxWithdrawValue = withdrawDialog.assetsToWithdrawFields.maxValue
  const targetName = getTargetDisplayName(target)
  const [isSuccess, setIsSuccess] = useState(false)
  const [migratedAmount, setMigratedAmount] = useState<NormalizedUnitNumber | null>(null)
  const destinationSelector = useMigrationDestinations(target)
  const receiver = target.vaultType === 'eLend' ? destinationSelector.selectedDestination?.subAccountAddress : undefined
  const enableAsCollateral =
    target.vaultType === 'eLend' && destinationSelector.selectedDestination?.collateralEnabled === false
  const selectorAssets = useMemo(() => {
    if (!usesWrappedNativeMigration) {
      return withdrawDialog.withdrawOptions
    }
    return withdrawDialog.withdrawOptions.filter(
      (option) => option.token.address.toLowerCase() === migrationToken.address.toLowerCase(),
    )
  }, [migrationToken.address, usesWrappedNativeMigration, withdrawDialog.withdrawOptions])

  useEffect(() => {
    if (!open || !usesWrappedNativeMigration) {
      return
    }

    if (withdrawDialog.form.getValues('symbol') === migrationToken.symbol) {
      return
    }

    withdrawDialog.form.setValue('symbol', migrationToken.symbol)
    withdrawDialog.form.clearErrors()
  }, [migrationToken.symbol, open, usesWrappedNativeMigration, withdrawDialog.form])

  useEffect(() => {
    if (!open || !maxWithdrawValue || maxWithdrawValue.lte(0)) {
      return
    }

    const maxValue = formFormat(maxWithdrawValue, migrationToken.decimals)
    const currentValue = withdrawDialog.form.getValues('value')
    const isMaxSelected = withdrawDialog.form.getValues('isMaxSelected')

    if (currentValue === maxValue && isMaxSelected) {
      return
    }

    withdrawDialog.form.setValue('value', maxValue, {
      shouldValidate: true,
    })
    withdrawDialog.form.setValue('isMaxSelected', true, {
      shouldValidate: true,
    })
  }, [maxWithdrawValue, migrationToken.decimals, open, withdrawDialog.form])

  useEffect(() => {
    if (open) {
      return
    }

    setIsSuccess(false)
    setMigratedAmount(null)
  }, [open])

  const objectives = createMigrationObjectives({
    withdrawObjectives: withdrawDialog.objectives,
    selectedWithdrawValue: withdrawDialog.withdrawAsset.value,
    enableAsCollateral,
    receiver,
    target,
    token: migrationToken,
  })
  const actionsEnabled =
    withdrawDialog.pageStatus.actionsEnabled &&
    objectives.length > withdrawDialog.objectives.length &&
    !destinationSelector.isLoading &&
    (target.vaultType !== 'eLend' || !!destinationSelector.selectedDestination)
  const successAmount = migratedAmount ?? withdrawDialog.withdrawAsset.value

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-describedby={undefined} className="max-w-xl">
        {isSuccess ? (
          <PooledMigrationSuccessView
            token={migrationToken}
            amount={successAmount}
            targetName={targetName}
            onProceed={() => setOpen(false)}
          />
        ) : (
          <div className="grid gap-4">
            <DialogTitle>Migrate {token.symbol}</DialogTitle>
            <div className="rounded-md border border-border bg-panel p-4">
              <Typography className="font-semibold text-sm">Move pooled deposits to Euler</Typography>
              <Typography variant="prompt" className="mt-2 text-sm leading-relaxed">
                This withdraws from pooled and deposits the same asset into:
              </Typography>
              <Typography className="mt-2 font-semibold text-primary text-sm">{`${migrationToken.symbol} - ${targetName}`}</Typography>
            </div>

            <FormAndOverviewWrapper>
              <DialogForm
                form={withdrawDialog.form}
                assetsFields={withdrawDialog.assetsToWithdrawFields}
                selectorAssets={selectorAssets}
                balanceType="protocol"
              />
              <WithdrawOverviewPanel
                withdrawAsset={withdrawDialog.withdrawAsset}
                currentPositionOverview={withdrawDialog.currentPositionOverview}
                updatedPositionOverview={withdrawDialog.updatedPositionOverview}
              />
            </FormAndOverviewWrapper>

            {withdrawDialog.riskAcknowledgement.warning && (
              <RiskAcknowledgement
                onStatusChange={withdrawDialog.riskAcknowledgement.onStatusChange}
                warning={withdrawDialog.riskAcknowledgement.warning}
              />
            )}

            {target.vaultType === 'eLend' && destinationSelector.destinations.length > 1 && (
              <DestinationSelector
                destinations={destinationSelector.destinations}
                selectedDestination={destinationSelector.selectedDestination}
                onSelect={destinationSelector.setSelectedDestinationId}
              />
            )}

            <DialogActionsPanel
              context={withdrawDialog.actionsContext}
              objectives={objectives}
              onFinish={() => {
                setMigratedAmount(withdrawDialog.withdrawAsset.value)
                setIsSuccess(true)
              }}
              enabled={actionsEnabled}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function PooledMigrationSuccessView({
  token,
  amount,
  targetName,
  onProceed,
}: {
  token: Token
  amount: NormalizedUnitNumber
  targetName: string
  onProceed: () => void
}) {
  return (
    <SuccessViewContent>
      <SuccessViewCheckmark isModal />
      <SuccessViewSummaryPanel title="Migrated">
        <div className="flex min-w-0 items-center gap-2">
          <TokenIcon token={token} className="h-6" />
          <div className="min-w-0">
            <div className="font-semibold">{token.symbol}</div>
            <Typography variant="prompt" className="truncate text-xs">
              {targetName}
            </Typography>
          </div>
        </div>
        <div className="flex grow flex-col">
          <Typography className="text-right font-primary">{token.format(amount, { style: 'auto' })}</Typography>
          <Typography variant="prompt" className="text-right">
            {token.formatUSD(amount)}
          </Typography>
        </div>
      </SuccessViewSummaryPanel>
      <SuccessViewProceedButton onProceed={onProceed}>Done</SuccessViewProceedButton>
    </SuccessViewContent>
  )
}

interface MigrationDestination {
  id: number
  label: string
  isNew: boolean
  collateralEnabled: boolean
  subAccountAddress: Address
  subtitle: string
  debtItems: MigrationDestinationDebtItem[]
}

interface MigrationDestinationDebtItem {
  symbol: string
  amount: string
}

interface MigrationDebtAccount {
  id: number
  controllers: Address[]
  subAccountAddress: Address
}

function DestinationSelector({
  destinations,
  selectedDestination,
  onSelect,
}: {
  destinations: MigrationDestination[]
  selectedDestination: MigrationDestination | undefined
  onSelect: (id: number) => void
}) {
  const [isSelectorOpen, setIsSelectorOpen] = useState(false)

  return (
    <div className="rounded-md border border-border bg-panel p-4">
      <Typography className="font-semibold text-sm">Select an Euler lending position for your deposit</Typography>
      <Dialog open={isSelectorOpen} onOpenChange={setIsSelectorOpen}>
        <button
          type="button"
          className="mt-3 flex w-full items-center justify-between rounded-md border border-white/10 bg-white/[0.03] px-3 py-2.5 text-left transition-colors hover:bg-white/[0.06]"
          onClick={() => setIsSelectorOpen(true)}
        >
          <DestinationLabel destination={selectedDestination} />
          <ChevronDown className="h-4 w-4 shrink-0 text-white/50" />
        </button>

        <DialogContent
          className="md:data-[state=closed]:slide-out-to-top-[16px] md:data-[state=open]:slide-in-from-top-[16px] border border-border bg-background px-6 pt-6 md:top-24 md:min-w-0 md:max-w-md md:pb-2"
          aria-describedby={undefined}
          contentVerticalPosition="top"
          overlayClassName="z-[240]"
          closeClassName="top-6"
        >
          <div className="mb-4">
            <DialogTitle className="mb-2">
              <Typography variant="p" className="font-bold text-lg" gradient>
                Select destination
              </Typography>
            </DialogTitle>
          </div>

          <div className="max-h-80 space-y-1 overflow-y-auto pb-4">
            {destinations.map((destination) => (
              <button
                key={`${destination.id}-${destination.isNew ? 'new' : 'existing'}`}
                type="button"
                className={cn(
                  'flex w-full items-center gap-3 rounded-md px-3 py-3 text-left transition-colors hover:bg-white/[0.04]',
                  selectedDestination?.id === destination.id && 'bg-primary/10',
                )}
                onClick={() => {
                  onSelect(destination.id)
                  setIsSelectorOpen(false)
                }}
              >
                <DestinationIcon destination={destination} />
                <div className="min-w-0 flex-1">
                  <div
                    className={cn('font-medium text-sm', selectedDestination?.id === destination.id && 'text-primary')}
                  >
                    {destination.label}
                  </div>
                  <DestinationDebtSummary destination={destination} />
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function DestinationLabel({ destination }: { destination: MigrationDestination | undefined }) {
  if (!destination) {
    return (
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5">
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex flex-col items-start">
          <span className="text-muted-foreground text-xs">Deposit to</span>
          <span className="text-muted-foreground text-sm">Select position</span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-w-0 items-center gap-3">
      <DestinationIcon destination={destination} />
      <div className="flex min-w-0 flex-col items-start">
        <span className="text-muted-foreground text-xs">Deposit to</span>
        <span className="truncate font-medium text-sm text-white">{destination.label}</span>
        <DestinationDebtSummary destination={destination} />
      </div>
    </div>
  )
}

function DestinationDebtSummary({ destination }: { destination: MigrationDestination }) {
  if (destination.isNew) {
    return <span className="text-primary/70 text-xs">{destination.subtitle}</span>
  }

  if (destination.debtItems.length === 0) {
    return <span className="text-muted-foreground text-xs">No debt</span>
  }

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-muted-foreground text-xs">Debt:</span>
      {destination.debtItems.map((debt) => (
        <span key={`${debt.symbol}-${debt.amount}`} className="flex items-center gap-1 text-muted-foreground text-xs">
          <TokenIcon token={{ symbol: TokenSymbol(debt.symbol) }} className="h-3.5 w-3.5" />
          {debt.amount} {debt.symbol}
        </span>
      ))}
    </div>
  )
}

function DestinationIcon({ destination }: { destination: MigrationDestination }) {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5">
      {destination.isNew ? (
        <Plus className="h-4 w-4 text-primary" />
      ) : (
        <span className="font-medium text-xs">#{destination.id}</span>
      )}
    </div>
  )
}

function useMigrationDestinations(target: PooledMigrationTarget) {
  const { address } = useAccount()
  const chainId = useChainId()
  const { activeSubaccountIds } = useSubaccountStore()
  const { getPositionName } = usePositionNames()
  const [selectedDestinationId, setSelectedDestinationId] = useState<number | null>(null)
  const isELendDestination = target.vaultType === 'eLend'
  const { pairs, isLoading: isPairsLoading } = useBorrowPairs(isELendDestination ? address : undefined)

  const idsToQuery = useMemo(() => {
    const fallbackIds = Array.from({ length: FALLBACK_SUBACCOUNT_RANGE }, (_, i) => i)
    return [...new Set([...fallbackIds, ...activeSubaccountIds])]
  }, [activeSubaccountIds])

  const { subaccountControllers, isLoading: isControllersLoading } = useSubaccountControllers({
    owner: address,
    subaccountIds: idsToQuery,
    enabled: !!address && isELendDestination,
  })

  const collateralContracts = useMemo(() => {
    if (!address || !isELendDestination) return []
    return idsToQuery.map((id) => ({
      address: EVC_ADDRESS as Address,
      abi: eulerVaultConnectorAbi,
      functionName: 'getCollaterals' as const,
      args: [getSubAccountAddress(address, id)] as const,
      chainId,
    }))
  }, [address, chainId, idsToQuery, isELendDestination])

  const { data: collateralData, isLoading: isCollateralsLoading } = useReadContracts({
    contracts: collateralContracts,
    query: {
      enabled: collateralContracts.length > 0,
      staleTime: 30_000,
    },
  })

  const subaccountCollaterals = useMemo(() => {
    const collaterals = new Map<number, Address[]>()
    idsToQuery.forEach((id, index) => {
      const result = collateralData?.[index]
      collaterals.set(id, result?.status === 'success' ? (result.result as Address[]) : [])
    })
    return collaterals
  }, [collateralData, idsToQuery])

  const destVaultMarket = useMemo(() => getVaultMarket(target.vault.address as Address), [target.vault.address])
  const compatibleControllerAddresses = useMemo(() => {
    const targetVaultAddress = target.vault.address.toLowerCase()
    return new Set(
      pairs
        .filter(
          (pair) =>
            pair.collateral.toLowerCase() === targetVaultAddress &&
            (pair.maxLTV ?? 0n) > 0n &&
            (pair.liquidationLTV ?? 0n) > 0n,
        )
        .map((pair) => pair.liability.toLowerCase()),
    )
  }, [pairs, target.vault.address])
  const controllerMetadata = useMemo(() => {
    const metadata = new Map<string, { symbol: string; decimals: number }>()
    for (const pair of pairs) {
      if (!pair.liabilitySymbol || pair.liabilityDecimals === undefined) {
        continue
      }

      metadata.set(pair.liability.toLowerCase(), {
        symbol: pair.liabilitySymbol,
        decimals: pair.liabilityDecimals,
      })
    }
    return metadata
  }, [pairs])

  const subAccountsWithDebt = useMemo(() => {
    if (!address || !isELendDestination) return []

    return [...subaccountControllers.entries()].flatMap(([id, controllers]) => {
      if (controllers.length === 0) return []
      const hasIncompatibleController =
        destVaultMarket &&
        controllers.some((controller) => {
          const market = getVaultMarket(controller)
          return market && market.key !== destVaultMarket.key
        })
      const hasUnsupportedController = controllers.some(
        (controller) => !compatibleControllerAddresses.has(controller.toLowerCase()),
      )
      if (hasIncompatibleController || hasUnsupportedController) return []
      return [{ id, controllers, subAccountAddress: getSubAccountAddress(address, id) }]
    })
  }, [address, compatibleControllerAddresses, destVaultMarket, isELendDestination, subaccountControllers])

  const debtContracts = useMemo(
    () =>
      subAccountsWithDebt.flatMap(({ controllers, subAccountAddress }) =>
        controllers.map((controller) => ({
          address: controller,
          abi: eVaultImplementationAbi,
          functionName: 'debtOf' as const,
          args: [subAccountAddress] as const,
          chainId,
        })),
      ),
    [chainId, subAccountsWithDebt],
  )

  const { data: debtData, isLoading: isDebtDataLoading } = useReadContracts({
    contracts: debtContracts,
    query: {
      enabled: debtContracts.length > 0,
      staleTime: 30_000,
    },
  })

  const destinations = useMemo(() => {
    if (!address || !isELendDestination) return []

    const destinations: MigrationDestination[] = []

    for (const [id, controllers] of subaccountControllers) {
      if (controllers.length === 0) {
        const collaterals = subaccountCollaterals.get(id) ?? []
        if (collaterals.length === 0) continue

        destinations.push({
          id,
          label: getPositionLabel(id, getPositionName(address, id)),
          isNew: false,
          collateralEnabled: collaterals.some(
            (collateral) => collateral.toLowerCase() === target.vault.address.toLowerCase(),
          ),
          subAccountAddress: getSubAccountAddress(address, id),
          subtitle: 'No debt',
          debtItems: [],
        })
        continue
      }

      const matchingDebtAccount = subAccountsWithDebt.find((account) => account.id === id)
      if (!matchingDebtAccount) continue
      const debtItems = getDestinationDebtItems({
        account: matchingDebtAccount,
        allDebtAccounts: subAccountsWithDebt,
        controllerMetadata,
        debtData,
      })

      destinations.push({
        id,
        label: getPositionLabel(id, getPositionName(address, id)),
        isNew: false,
        collateralEnabled: (subaccountCollaterals.get(id) ?? []).some(
          (collateral) => collateral.toLowerCase() === target.vault.address.toLowerCase(),
        ),
        subAccountAddress: matchingDebtAccount.subAccountAddress,
        subtitle: 'Existing position',
        debtItems,
      })
    }

    const idsInUse = [...subaccountControllers.entries()]
      .filter(([id, controllers]) => controllers.length > 0 || (subaccountCollaterals.get(id)?.length ?? 0) > 0)
      .map(([id]) => id)
    const nextAvailableId = getFirstAvailableSubaccountId(idsInUse)

    if (nextAvailableId !== -1 && destinations.length < FALLBACK_SUBACCOUNT_RANGE) {
      destinations.push({
        id: nextAvailableId,
        label: 'New Position',
        isNew: true,
        collateralEnabled: false,
        subAccountAddress: getSubAccountAddress(address, nextAvailableId),
        subtitle: 'Creates a new sub-account',
        debtItems: [],
      })
    }

    return destinations
  }, [
    address,
    controllerMetadata,
    debtData,
    getPositionName,
    isELendDestination,
    subaccountCollaterals,
    subaccountControllers,
    subAccountsWithDebt,
    target.vault.address,
  ])

  useEffect(() => {
    if (!isELendDestination || selectedDestinationId !== null || destinations.length === 0) return

    const subAccount0 = destinations.find((destination) => destination.id === 0 && !destination.isNew)
    setSelectedDestinationId((subAccount0 ?? destinations[0]!).id)
  }, [destinations, isELendDestination, selectedDestinationId])

  const selectedDestination = useMemo(
    () => destinations.find((destination) => destination.id === selectedDestinationId) ?? destinations[0],
    [destinations, selectedDestinationId],
  )

  return {
    destinations,
    selectedDestination,
    setSelectedDestinationId,
    isLoading:
      isControllersLoading ||
      isCollateralsLoading ||
      isPairsLoading ||
      isDebtDataLoading,
  }
}

function getPositionLabel(subAccountId: number, customName?: string): string {
  return customName || `Position ${subAccountId}`
}

function getMigrationToken({
  token,
  target,
  chainId,
}: {
  token: Token
  target: PooledMigrationTarget
  chainId: number
}): Token {
  if (!isNativeHypeAddress(token.address) || !isWhypeAddress(target.vault.underlyingAsset, chainId)) {
    return token
  }

  return token.clone({
    address: CheckedAddress(target.vault.underlyingAsset),
    symbol: TokenSymbol('WHYPE'),
    name: 'Wrapped HYPE',
  })
}

function getDestinationDebtItems({
  account,
  allDebtAccounts,
  controllerMetadata,
  debtData,
}: {
  account: MigrationDebtAccount
  allDebtAccounts: MigrationDebtAccount[]
  controllerMetadata: Map<string, { symbol: string; decimals: number }>
  debtData:
    | {
        result?: unknown
      }[]
    | undefined
}): MigrationDestinationDebtItem[] {
  const flatOffset = allDebtAccounts
    .slice(
      0,
      allDebtAccounts.findIndex((debtAccount) => debtAccount.id === account.id),
    )
    .reduce((sum, debtAccount) => sum + debtAccount.controllers.length, 0)

  return account.controllers.flatMap((controller, index) => {
    const debtRaw = debtData?.[flatOffset + index]?.result
    if (typeof debtRaw !== 'bigint' || debtRaw === 0n) {
      return []
    }

    const metadata = controllerMetadata.get(controller.toLowerCase())
    const symbol = metadata?.symbol ?? 'UNKNOWN'
    const decimals = metadata?.decimals ?? 18

    return [
      {
        symbol,
        amount: formatDebtAmount(formatUnits(debtRaw, decimals)),
      },
    ]
  })
}

function formatDebtAmount(value: string): string {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) {
    return value
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: numericValue >= 1 ? 2 : 4,
  }).format(numericValue)
}

function getTargetDisplayName(target: PooledMigrationTarget): string {
  if (target.label === 'Earn') {
    return 'HypurrFi Earn'
  }

  return `HypurrFi ${target.label}`
}

function createMigrationObjectives({
  withdrawObjectives,
  selectedWithdrawValue,
  enableAsCollateral,
  receiver,
  target,
  token,
}: {
  withdrawObjectives: Objective[]
  selectedWithdrawValue: NormalizedUnitNumber
  enableAsCollateral: boolean
  receiver?: Address
  target: PooledMigrationTarget
  token: Token
}): Objective[] {
  if (selectedWithdrawValue.lte(0)) {
    return withdrawObjectives
  }

  return [
    ...withdrawObjectives,
    {
      type: 'vaultDeposit',
      // EEARN routes to the EVC/Permit2 deposit path in useCreateActions — used for both eEarn and eLend targets here.
      vaultType: VaultType.EEARN,
      token,
      value: selectedWithdrawValue,
      vaultAddress: target.vault.address,
      tellerVaultAddress: target.vault.address,
      tokenOutAddress: target.vault.underlyingAsset,
      rateInQuote: '1',
      swapPath: '0x',
      evcAddress: EVC_ADDRESS,
      receiver,
      enableAsCollateral,
    },
  ]
}

export const pooledMigrationDialogConfig: DialogConfig<PooledMigrationDialogProps> = {
  options: {
    closeOnChainChange: true,
  },
  element: PooledMigrationDialog,
}
